import * as Sentry from "@sentry/react";

type ErrorSeverity = "low" | "medium" | "high" | "critical";

interface ErrorLogData {
  message: string;
  severity: ErrorSeverity;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: Error;
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private readonly maxLogSize = 100;
  private errorLog: ErrorLogData[] = [];

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  public logError(
    message: string,
    severity: ErrorSeverity,
    context?: Record<string, unknown>,
    error?: Error
  ): void {
    const errorData: ErrorLogData = {
      message,
      severity,
      timestamp: new Date().toISOString(),
      context,
      error,
    };

    // Add to log
    this.errorLog.unshift(errorData);

    // Keep log size manageable
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // Log to console in development
    if (process.env["NODE_ENV"] === "development") {
      console.error(
        `[${errorData.severity.toUpperCase()}] ${errorData.message}`,
        {
          timestamp: errorData.timestamp,
          context: errorData.context,
          error: errorData.error,
        }
      );
    }

    // Handle critical errors
    if (severity === "critical") {
      this.handleCriticalError(errorData);
    }
  }

  public getRecentErrors(count = 10): ErrorLogData[] {
    return this.errorLog.slice(0, count);
  }

  public clearLog(): void {
    this.errorLog = [];
  }

  private handleCriticalError(errorData: ErrorLogData): void {
    // Send to monitoring service or take appropriate action
    console.error("[CRITICAL ERROR]", errorData);

    Sentry.captureException(errorData.error || new Error(errorData.message), {
      level: "fatal",
      extra: errorData.context,
      tags: { severity: errorData.severity },
    });
  }
}

// Export singleton instance
export const errorLogger = ErrorLogger.getInstance();

// Utility functions
export function logError(
  message: string,
  severity: ErrorSeverity = "medium",
  context?: Record<string, unknown>,
  error?: Error
): void {
  errorLogger.logError(message, severity, context, error);
}

export function handleError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (error instanceof Error) {
    logError(error.message, "high", context, error);
  } else {
    logError("An unknown error occurred", "high", {
      ...context,
      errorValue: error,
    });
  }
}

// Global error boundary handler
export function handleGlobalError(
  error: Error,
  errorInfo: React.ErrorInfo
): void {
  logError(
    "Uncaught error in component",
    "critical",
    {
      componentStack: errorInfo.componentStack,
    },
    error
  );
}
